import type { FormFieldMeta } from "@/shared/lib/forms";

/**
 * A field whose values are loaded ahead of time and used once.
 *
 * Reading the attribute's config and turning the field into a picker. The
 * picker is UX only: what a value is allowed to be is enforced when the record
 * is saved, not here.
 */

export type ValueStockOnVoid = "release" | "retire";

/**
 * Only types where a fixed list already makes sense. A pool is a dynamic,
 * scoped, consumable version of the same idea, so the same types qualify.
 */
const STOCKABLE_FIELD_TYPES = new Set(["TEXT", "NUMBER"]);

export function canUseLoadedValues(fieldType: string | undefined): boolean {
  return STOCKABLE_FIELD_TYPES.has(fieldType ?? "");
}

export type ValueStockConfig = {
  enabled: true;
  scope_entity_key: string;
  on_void: ValueStockOnVoid;
};

export type QrLabelConfig = {
  enabled: true;
  public_fields: string[];
};

export function readQrConfig(
  configurationData: Record<string, unknown> | undefined | null,
): QrLabelConfig | null {
  const raw = configurationData?.qr;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const rec = raw as Record<string, unknown>;
  if (!rec.enabled) return null;
  const fields = rec.public_fields;
  return {
    enabled: true,
    public_fields: Array.isArray(fields)
      ? fields.map((code) => String(code).trim()).filter(Boolean)
      : [],
  };
}

export function readStockConfig(
  configurationData: Record<string, unknown> | undefined | null,
): ValueStockConfig | null {
  const stock = configurationData?.value_stock;
  if (!stock || typeof stock !== "object" || Array.isArray(stock)) return null;
  const rec = stock as Record<string, unknown>;
  if (!rec.enabled) return null;
  return {
    enabled: true,
    scope_entity_key: String(rec.scope_entity_key || "").trim(),
    on_void: rec.on_void === "retire" ? "retire" : "release",
  };
}

/** Turn a stock-backed field into a picker over the values left for this scope. */
export function asStockSearchField(
  meta: FormFieldMeta,
  scopeUuid: string | null | undefined,
  copy?: { placeholder?: string },
): FormFieldMeta {
  const stock = readStockConfig(meta.configuration_data);
  if (!stock || !scopeUuid) return meta;
  return {
    ...meta,
    field_type: "ENTITY_SEARCH",
    configuration_data: {
      ...(meta.configuration_data ?? {}),
      lookup_provider: "value_stock",
      provider_params: {
        entity: "holding",
        attribute: meta.code_name,
        scope_entity: stock.scope_entity_key || "product",
        scope_id: scopeUuid,
        status: "available",
      },
      // Free text would bypass the stock entirely.
      allow_create: false,
      icon: "hash",
      ...(copy?.placeholder ? { placeholder: copy.placeholder } : {}),
    },
  };
}

/** The picker holds ``{uuid, name}``; the record stores the bare value. */
export function wrapStockFormValue(
  value: unknown,
): { uuid: string; name: string } | null {
  if (value == null || value === "") return null;
  if (typeof value === "object" && !Array.isArray(value)) {
    const rec = value as Record<string, unknown>;
    const name = String(rec.name ?? rec.uuid ?? "").trim();
    if (!name) return null;
    return { uuid: String(rec.uuid ?? name), name };
  }
  const text = String(value).trim();
  return text ? { uuid: text, name: text } : null;
}

export function unwrapStockFormValue(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value === "object" && !Array.isArray(value)) {
    const rec = value as Record<string, unknown>;
    const name = rec.name ?? rec.uuid ?? rec.value;
    return name == null ? "" : String(name);
  }
  return String(value);
}

export function unwrapStockAttributeValues(
  values: Record<string, unknown>,
  fields: {
    code_name: string;
    configuration_data?: Record<string, unknown> | null;
  }[],
): Record<string, unknown> {
  const stocked = new Set(
    fields
      .filter((field) => readStockConfig(field.configuration_data))
      .map((field) => field.code_name),
  );
  if (stocked.size === 0) return values;
  const next = { ...values };
  for (const key of stocked) {
    if (key in next) next[key] = unwrapStockFormValue(next[key]);
  }
  return next;
}
